import {
  localize,
  type Automation,
  type AutomationActionResource,
  type AutomationConditionField,
  type AutomationConditionOperator,
  type AutomationConditionResource,
  type AutomationStatus,
  type AutomationTriggerType,
  type CreateAutomationInput,
  type LocalizedText,
} from '@neuroedge/shared';

/**
 * Pure model behind the simplified "When / If / Then" automation form.
 *
 * The API shape is unchanged (triggers + OR-groups-of-AND conditions + typed
 * actions); this module maps it to/from a friendlier form state:
 * - **Rules** instead of raw conditions, combined by one "all / any" switch
 *   ("all" = one AND group, "any" = one OR group per rule). Anything that
 *   doesn't fit that shape (mixed AND/OR groups, an unfamiliar field/operator
 *   combination) loads in "advanced rules" mode, edited as raw conditions -
 *   never silently rewritten.
 * - One **Reply** action (text + optional buttons or list) instead of
 *   SEND_TEXT / SEND_TEXT_WITH_BUTTONS / SEND_LIST, chosen on save by which
 *   reply options are present.
 * - Button/list ids generated from their labels.
 */

// --- Triggers ---------------------------------------------------------------

export const TRIGGERS: { value: AutomationTriggerType; title: string; hint: string }[] = [
  {
    value: 'CONVERSATION_CREATED',
    title: 'A customer messages for the first time',
    hint: 'A brand-new conversation starts - good for a welcome message.',
  },
  {
    value: 'NEW_MESSAGE',
    title: 'A customer sends a message',
    hint: 'Any incoming message - narrow it down with rules below.',
  },
  {
    value: 'FOLLOW_UP_DUE',
    title: 'A follow-up is due',
    hint: 'Runs on schedule when a follow-up reaches its due time.',
  },
  {
    value: 'TASK_DUE',
    title: 'A task is due',
    hint: 'Runs on schedule when a task reaches its due time.',
  },
];

export const TIME_BASED_TRIGGERS = new Set<AutomationTriggerType>(['FOLLOW_UP_DUE', 'TASK_DUE']);

// --- Rules ------------------------------------------------------------------

export type RuleKind = 'keywords' | 'exact' | 'contains' | 'not_contains' | 'button' | 'media';

export interface Rule {
  kind: RuleKind;
  /** keywords: the chips; every other kind: one value in `keywords[0]`. */
  keywords: string[];
}

export const RULE_KINDS: { kind: RuleKind; label: string; addable: boolean }[] = [
  { kind: 'keywords', label: 'Message contains any of these words', addable: true },
  { kind: 'exact', label: 'Message is exactly', addable: true },
  { kind: 'not_contains', label: 'Message does not contain', addable: true },
  { kind: 'button', label: 'Customer tapped a button / list option', addable: true },
  { kind: 'media', label: 'Customer sent a', addable: true },
  // Only produced when loading an existing CONTAINS rule (case-sensitive, one value).
  { kind: 'contains', label: 'Message contains (exact case)', addable: false },
];

export const MEDIA_TYPES: { value: string; label: string }[] = [
  { value: 'IMAGE', label: 'Photo' },
  { value: 'DOCUMENT', label: 'Document / PDF' },
  { value: 'AUDIO', label: 'Voice note / audio' },
  { value: 'VIDEO', label: 'Video' },
];

const RULE_TO_CONDITION: Record<
  RuleKind,
  { field: AutomationConditionField; operator: AutomationConditionOperator }
> = {
  keywords: { field: 'message.text', operator: 'CONTAINS_ANY' },
  exact: { field: 'message.text', operator: 'EQUALS' },
  contains: { field: 'message.text', operator: 'CONTAINS' },
  not_contains: { field: 'message.text', operator: 'NOT_CONTAINS' },
  button: { field: 'message.replyPayload', operator: 'EQUALS' },
  media: { field: 'message.type', operator: 'EQUALS' },
};

function conditionToRule(c: {
  field: string;
  operator: string;
  value: string | null;
}): Rule | null {
  const kind = (Object.keys(RULE_TO_CONDITION) as RuleKind[]).find(
    (k) => RULE_TO_CONDITION[k].field === c.field && RULE_TO_CONDITION[k].operator === c.operator,
  );
  if (!kind) return null;
  const value = c.value ?? '';
  return {
    kind,
    keywords:
      kind === 'keywords'
        ? value
            .split('\n')
            .map((k) => k.trim())
            .filter(Boolean)
        : [value],
  };
}

export function blankRule(kind: RuleKind): Rule {
  return { kind, keywords: kind === 'media' ? ['IMAGE'] : kind === 'keywords' ? [] : [''] };
}

/** A raw condition row, for the advanced (OR-groups-of-AND) editor. */
export interface RawCondition {
  field: AutomationConditionField;
  operator: AutomationConditionOperator;
  value: string;
}

export const VALUE_LESS_OPERATORS = new Set<AutomationConditionOperator>(['IS_SET', 'IS_NOT_SET']);

// --- Actions ----------------------------------------------------------------

export interface ReplyOption {
  id: string;
  title: LocalizedText;
  description?: LocalizedText;
}

export type ActionKind = 'reply' | 'image' | 'template' | 'flow' | 'lead' | 'quotation';

export interface ActionDraft {
  kind: ActionKind;
  // reply
  text: LocalizedText;
  replyMode: 'none' | 'buttons' | 'list';
  buttons: ReplyOption[];
  listButtonText: LocalizedText;
  /** List sections; a single untitled section is a plain list. */
  sections: { title?: LocalizedText; rows: ReplyOption[] }[];
  // image
  imageUrl: string;
  caption: LocalizedText;
  // reply/image: sent instead outside WhatsApp's 24h window
  fallbackTemplateName: string;
  // template
  templateName: string;
  variables: Record<string, string>;
  // flow
  flowKey: string;
  // lead
  pipelineId: string;
  stageId: string;
  leadTitle: string;
  leadSource: string;
}

export const ACTION_KINDS: { kind: ActionKind; title: string; hint: string }[] = [
  {
    kind: 'reply',
    title: 'Reply with a message',
    hint: 'Text, optionally with buttons or a list to tap.',
  },
  {
    kind: 'flow',
    title: 'Start a guided conversation',
    hint: 'Booking, ordering, and other flows.',
  },
  { kind: 'lead', title: 'Add them as a lead', hint: 'Creates a lead in a pipeline you choose.' },
  { kind: 'image', title: 'Send an image', hint: 'A picture from a web link, with a caption.' },
  {
    kind: 'template',
    title: 'Send an approved template',
    hint: 'A Meta-approved message - works even after 24 hours.',
  },
  {
    kind: 'quotation',
    title: 'Make an AI quotation',
    hint: 'Reads the photo/list the customer sent and replies with a priced PDF.',
  },
];

export function blankAction(kind: ActionKind): ActionDraft {
  return {
    kind,
    text: '',
    replyMode: 'none',
    buttons: [],
    listButtonText: 'Choose',
    sections: [{ rows: [] }],
    imageUrl: '',
    caption: '',
    fallbackTemplateName: '',
    templateName: '',
    variables: {},
    flowKey: '',
    pipelineId: '',
    stageId: '',
    leadTitle: '',
    leadSource: '',
  };
}

export function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return slug || 'option';
}

export function uniqueId(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n += 1) if (!used.has(`${base}_${n}`)) return `${base}_${n}`;
}

function asLocalized(value: unknown): LocalizedText {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value as Record<string, string>;
  return '';
}

function actionFromResource(
  a: Pick<AutomationActionResource, 'actionType' | 'config'>,
): ActionDraft {
  const cfg = a.config as Record<string, unknown>;
  const fallback = (cfg.fallbackTemplateName as string | undefined) ?? '';
  switch (a.actionType) {
    case 'SEND_TEXT':
      return {
        ...blankAction('reply'),
        text: asLocalized(cfg.text),
        fallbackTemplateName: fallback,
      };
    case 'SEND_TEXT_WITH_BUTTONS':
      return {
        ...blankAction('reply'),
        text: asLocalized(cfg.text),
        replyMode: 'buttons',
        buttons: ((cfg.buttons as ReplyOption[] | undefined) ?? []).map((b) => ({
          id: b.id,
          title: asLocalized(b.title),
        })),
        fallbackTemplateName: fallback,
      };
    case 'SEND_LIST':
      return {
        ...blankAction('reply'),
        text: asLocalized(cfg.text),
        replyMode: 'list',
        listButtonText: asLocalized(cfg.buttonText),
        sections: (
          (cfg.sections as { title?: unknown; rows: ReplyOption[] }[] | undefined) ?? [{ rows: [] }]
        ).map((s) => ({
          ...(s.title !== undefined ? { title: asLocalized(s.title) } : {}),
          rows: s.rows.map((r) => ({
            id: r.id,
            title: asLocalized(r.title),
            ...(r.description !== undefined ? { description: asLocalized(r.description) } : {}),
          })),
        })),
        fallbackTemplateName: fallback,
      };
    case 'SEND_IMAGE':
      return {
        ...blankAction('image'),
        imageUrl: (cfg.imageUrl as string) ?? '',
        caption: asLocalized(cfg.caption),
        fallbackTemplateName: fallback,
      };
    case 'SEND_WHATSAPP_TEMPLATE':
      return {
        ...blankAction('template'),
        templateName: (cfg.templateName as string) ?? '',
        variables: { ...((cfg.variables as Record<string, string> | undefined) ?? {}) },
      };
    case 'START_FLOW':
      return { ...blankAction('flow'), flowKey: (cfg.flowKey as string) ?? '' };
    case 'CREATE_LEAD':
      return {
        ...blankAction('lead'),
        pipelineId: (cfg.pipelineId as string) ?? '',
        stageId: (cfg.stageId as string) ?? '',
        leadTitle: (cfg.title as string) ?? '',
        leadSource: (cfg.source as string) ?? '',
      };
    case 'GENERATE_QUOTATION':
      return blankAction('quotation');
  }
}

// --- Whole form -------------------------------------------------------------

export interface AutomationFormState {
  name: string;
  description: string;
  channelId: string;
  status: AutomationStatus;
  triggers: AutomationTriggerType[];
  /** `simple`: `rules` + `match`; `advanced`: raw OR-groups of AND conditions. */
  rulesMode: 'simple' | 'advanced';
  match: 'all' | 'any';
  rules: Rule[];
  conditionGroups: RawCondition[][];
  actions: ActionDraft[];
}

export function blankForm(): AutomationFormState {
  return {
    name: '',
    description: '',
    channelId: '',
    status: 'ACTIVE',
    triggers: ['NEW_MESSAGE'],
    rulesMode: 'simple',
    match: 'any',
    rules: [],
    conditionGroups: [],
    actions: [blankAction('reply')],
  };
}

function groupConditions(
  conditions: AutomationConditionResource[],
): AutomationConditionResource[][] {
  const groups = new Map<number, AutomationConditionResource[]>();
  for (const c of [...conditions].sort(
    (a, b) => a.groupIndex - b.groupIndex || a.position - b.position,
  )) {
    groups.set(c.groupIndex, [...(groups.get(c.groupIndex) ?? []), c]);
  }
  return [...groups.keys()].sort((a, b) => a - b).map((k) => groups.get(k)!);
}

export function formFromAutomation(a: Automation): AutomationFormState {
  const groups = groupConditions(a.conditions);
  const conditionGroups = groups.map((g) =>
    g.map((c) => ({ field: c.field, operator: c.operator, value: c.value ?? '' })),
  );

  let rulesMode: AutomationFormState['rulesMode'] = 'simple';
  let match: AutomationFormState['match'] = 'any';
  let rules: Rule[] = [];
  if (groups.length === 1) {
    match = 'all';
    rules = groups[0]!.map((c) => conditionToRule(c)).filter((r): r is Rule => r !== null);
    if (rules.length !== groups[0]!.length) rulesMode = 'advanced';
  } else if (groups.length > 1) {
    if (groups.every((g) => g.length === 1)) {
      rules = groups.map((g) => conditionToRule(g[0]!)).filter((r): r is Rule => r !== null);
      if (rules.length !== groups.length) rulesMode = 'advanced';
    } else {
      rulesMode = 'advanced';
    }
  }

  return {
    name: a.name,
    description: a.description ?? '',
    channelId: a.channelId ?? '',
    status: a.status,
    triggers: a.triggers.length ? a.triggers.map((t) => t.triggerType) : ['NEW_MESSAGE'],
    rulesMode,
    match,
    rules: rulesMode === 'simple' ? rules : [],
    conditionGroups,
    actions: a.actions.length
      ? [...a.actions].sort((x, y) => x.position - y.position).map(actionFromResource)
      : [blankAction('reply')],
  };
}

/** The When / If / Then part of an automation - what the JSON view edits. */
export interface AutomationDefinition {
  channelId?: string | null;
  triggers: { triggerType: AutomationTriggerType }[];
  conditions?: {
    groupIndex: number;
    position: number;
    field: AutomationConditionField;
    operator: AutomationConditionOperator;
    value?: string | null;
  }[];
  actions: {
    position: number;
    actionType: AutomationActionResource['actionType'];
    config: unknown;
  }[];
}

/** The form's When / If / Then as JSON (the API's shape). */
export function definitionFromForm(state: AutomationFormState): AutomationDefinition {
  const p = toPayload(state);
  return {
    channelId: p.channelId ?? null,
    triggers: p.triggers,
    conditions: p.conditions ?? [],
    actions: p.actions,
  };
}

/**
 * Parsed JSON back into form state, keeping name / description / status from
 * `base`. Null when it isn't an automation definition.
 */
export function formFromDefinition(
  base: AutomationFormState,
  value: unknown,
): AutomationFormState | null {
  const d = value as Partial<AutomationDefinition> | null;
  if (!d || typeof d !== 'object' || !Array.isArray(d.triggers) || !Array.isArray(d.actions)) {
    return null;
  }
  if (d.conditions !== undefined && !Array.isArray(d.conditions)) return null;
  const next = formFromAutomation({
    id: '',
    name: base.name,
    description: base.description || null,
    channelId: d.channelId ?? null,
    status: base.status,
    version: 0,
    triggers: d.triggers.map((t, i) => ({ id: String(i), triggerType: t.triggerType })),
    conditions: (d.conditions ?? []).map((c, i) => ({
      id: String(i),
      groupIndex: c.groupIndex,
      position: c.position,
      field: c.field,
      operator: c.operator,
      value: c.value ?? null,
    })),
    actions: d.actions.map((a, i) => ({
      id: String(i),
      position: a.position ?? i,
      actionType: a.actionType,
      config: (a.config ?? {}) as Record<string, unknown>,
    })),
    createdAt: '',
    updatedAt: '',
  });
  return { ...next, name: base.name, description: base.description, status: base.status };
}

export function textOf(value: LocalizedText | undefined): string {
  return value === undefined ? '' : localize(value, undefined);
}

function trimLocalized(value: LocalizedText): LocalizedText {
  if (typeof value === 'string') return value.trim();
  const out = Object.fromEntries(
    Object.entries(value)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v !== ''),
  );
  const keys = Object.keys(out);
  if (keys.length === 1 && keys[0] === 'en') return out.en!;
  return out;
}

function optionalLocalized(value: LocalizedText | undefined): LocalizedText | undefined {
  return value !== undefined && textOf(value).trim() ? trimLocalized(value) : undefined;
}

export function messageTriggers(triggers: AutomationTriggerType[]): boolean {
  return triggers.some((t) => !TIME_BASED_TRIGGERS.has(t));
}

function buildConditions(state: AutomationFormState): CreateAutomationInput['conditions'] {
  // Rules only make sense for message triggers - a time-based-only automation has no message.
  if (!messageTriggers(state.triggers)) return [];
  if (state.rulesMode === 'advanced') {
    return state.conditionGroups.flatMap((group, groupIndex) =>
      group.map((c, position) => ({
        groupIndex,
        position,
        field: c.field,
        operator: c.operator,
        ...(VALUE_LESS_OPERATORS.has(c.operator) ? {} : { value: c.value.trim() }),
      })),
    );
  }
  return state.rules.map((rule, i) => {
    const { field, operator } = RULE_TO_CONDITION[rule.kind];
    const value =
      rule.kind === 'keywords'
        ? rule.keywords
            .map((k) => k.trim())
            .filter(Boolean)
            .join('\n')
        : (rule.keywords[0] ?? '').trim();
    return {
      groupIndex: state.match === 'all' ? 0 : i,
      position: state.match === 'all' ? i : 0,
      field,
      operator,
      value,
    };
  });
}

function buildAction(a: ActionDraft, position: number): CreateAutomationInput['actions'][number] {
  const fallback = a.fallbackTemplateName.trim()
    ? { fallbackTemplateName: a.fallbackTemplateName.trim() }
    : {};
  switch (a.kind) {
    case 'reply':
      if (a.replyMode === 'buttons' && a.buttons.length) {
        return {
          position,
          actionType: 'SEND_TEXT_WITH_BUTTONS',
          config: {
            text: trimLocalized(a.text),
            buttons: a.buttons.map((b) => ({ id: b.id, title: trimLocalized(b.title) })),
            ...fallback,
          },
        };
      }
      if (a.replyMode === 'list') {
        return {
          position,
          actionType: 'SEND_LIST',
          config: {
            text: trimLocalized(a.text),
            buttonText: trimLocalized(a.listButtonText),
            sections: a.sections
              .filter((s) => s.rows.length)
              .map((s) => {
                const title = optionalLocalized(s.title);
                return {
                  ...(title !== undefined ? { title } : {}),
                  rows: s.rows.map((r) => {
                    const description = optionalLocalized(r.description);
                    return {
                      id: r.id,
                      title: trimLocalized(r.title),
                      ...(description !== undefined ? { description } : {}),
                    };
                  }),
                };
              }),
            ...fallback,
          },
        };
      }
      return {
        position,
        actionType: 'SEND_TEXT',
        config: { text: trimLocalized(a.text), ...fallback },
      };
    case 'image': {
      const caption = optionalLocalized(a.caption);
      return {
        position,
        actionType: 'SEND_IMAGE',
        config: {
          imageUrl: a.imageUrl.trim(),
          ...(caption !== undefined ? { caption } : {}),
          ...fallback,
        },
      };
    }
    case 'template': {
      const variables = Object.fromEntries(
        Object.entries(a.variables)
          .map(([k, v]) => [k, v.trim()] as const)
          .filter(([, v]) => v !== ''),
      );
      return {
        position,
        actionType: 'SEND_WHATSAPP_TEMPLATE',
        config: {
          templateName: a.templateName.trim(),
          ...(Object.keys(variables).length ? { variables } : {}),
        },
      };
    }
    case 'flow':
      return { position, actionType: 'START_FLOW', config: { flowKey: a.flowKey } };
    case 'lead':
      return {
        position,
        actionType: 'CREATE_LEAD',
        config: {
          pipelineId: a.pipelineId,
          stageId: a.stageId,
          ...(a.leadTitle.trim() ? { title: a.leadTitle.trim() } : {}),
          ...(a.leadSource.trim() ? { source: a.leadSource.trim() } : {}),
        },
      };
    case 'quotation':
      return { position, actionType: 'GENERATE_QUOTATION', config: {} };
  }
}

export function toPayload(state: AutomationFormState): CreateAutomationInput {
  const timeBased = state.triggers.some((t) => TIME_BASED_TRIGGERS.has(t));
  return {
    name: state.name.trim(),
    description: state.description.trim() || null,
    channelId: timeBased ? null : state.channelId || null,
    status: state.status,
    triggers: state.triggers.map((triggerType) => ({ triggerType })),
    conditions: buildConditions(state),
    actions: state.actions.map(buildAction),
  };
}

// --- Validation -------------------------------------------------------------

function tooLong(value: LocalizedText | undefined, max: number): boolean {
  if (value === undefined) return false;
  return (typeof value === 'string' ? [value] : Object.values(value)).some((v) => v.length > max);
}

/**
 * Client-side checks mirroring the server's schema, keyed the way the API's
 * field errors are (`conditions/<i>/value`, `actions/<i>/text`, ...) so
 * server errors land on the same fields. Rule `i` is condition `i` in both
 * match modes (see `buildConditions`).
 */
export function validateForm(state: AutomationFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = state.name.trim();
  if (!name) errors.name = 'Give this automation a name.';
  else if (name.length > 255) errors.name = 'Name must be 255 characters or fewer.';
  if (state.description.trim().length > 2000) {
    errors.description = 'Description must be 2000 characters or fewer.';
  }
  if (state.triggers.length === 0) errors.triggers = 'Pick at least one "When".';
  if (state.actions.length === 0) errors.actions = 'Add at least one thing to do.';

  if (messageTriggers(state.triggers)) {
    if (state.rulesMode === 'simple') {
      state.rules.forEach((rule, i) => {
        const key = `conditions/${i}/value`;
        if (rule.kind === 'keywords') {
          const words = rule.keywords.map((k) => k.trim()).filter(Boolean);
          if (!words.length) errors[key] = 'Add at least one word.';
          else if (words.join('\n').length > 1024)
            errors[key] = 'Too many words (1024 characters max).';
        } else {
          const value = (rule.keywords[0] ?? '').trim();
          if (!value) errors[key] = 'Enter a value.';
          else if (value.length > 1024) errors[key] = 'Must be 1024 characters or fewer.';
        }
      });
    } else {
      let i = 0;
      for (const group of state.conditionGroups) {
        for (const c of group) {
          if (!VALUE_LESS_OPERATORS.has(c.operator) && !c.value.trim()) {
            errors[`conditions/${i}/value`] = 'Enter a value.';
          }
          i += 1;
        }
      }
    }
  }

  state.actions.forEach((a, i) => {
    const k = (field: string) => `actions/${i}/${field}`;
    if (a.kind === 'reply') {
      const max = a.replyMode === 'none' ? 4096 : 1024;
      if (!textOf(a.text).trim()) errors[k('text')] = 'Write the message.';
      else if (tooLong(a.text, max))
        errors[k('text')] = `Message must be at most ${max} characters.`;
      if (a.replyMode === 'buttons') {
        if (a.buttons.length < 1 || a.buttons.length > 3)
          errors[k('buttons')] = 'Add 1 to 3 buttons.';
        a.buttons.forEach((b, bi) => {
          if (!textOf(b.title).trim()) errors[k(`buttons/${bi}/title`)] = 'Enter a button label.';
          else if (tooLong(b.title, 20)) errors[k(`buttons/${bi}/title`)] = 'Max 20 characters.';
        });
      }
      if (a.replyMode === 'list') {
        if (!textOf(a.listButtonText).trim())
          errors[k('buttonText')] = 'Enter the list button label.';
        else if (tooLong(a.listButtonText, 20)) errors[k('buttonText')] = 'Max 20 characters.';
        const rows = a.sections.flatMap((s) => s.rows);
        if (rows.length < 1 || rows.length > 10) errors[k('sections')] = 'Add 1 to 10 options.';
        else if (rows.some((r) => !textOf(r.title).trim() || tooLong(r.title, 24))) {
          errors[k('sections')] = 'Every option needs a title of up to 24 characters.';
        } else if (rows.some((r) => tooLong(r.description, 72))) {
          errors[k('sections')] = 'Option descriptions can be at most 72 characters.';
        }
      }
    } else if (a.kind === 'image') {
      try {
        new URL(a.imageUrl.trim());
      } catch {
        errors[k('imageUrl')] = 'Enter a valid image link (https://…).';
      }
      if (tooLong(a.caption, 1024))
        errors[k('caption')] = 'Caption must be at most 1024 characters.';
    } else if (a.kind === 'template') {
      if (!a.templateName.trim()) errors[k('templateName')] = 'Choose a template.';
    } else if (a.kind === 'flow') {
      if (!a.flowKey) errors[k('flowKey')] = 'Choose a conversation flow.';
    } else if (a.kind === 'lead') {
      if (!a.pipelineId) errors[k('pipelineId')] = 'Choose a pipeline.';
      if (!a.stageId) errors[k('stageId')] = 'Choose a stage.';
      if (a.leadTitle.trim().length > 200) errors[k('title')] = 'Max 200 characters.';
      if (a.leadSource.trim().length > 120) errors[k('source')] = 'Max 120 characters.';
    }
    if (a.fallbackTemplateName.trim().length > 512) {
      errors[k('fallbackTemplateName')] = 'Template name must be 512 characters or fewer.';
    }
  });
  return errors;
}

// --- Plain-English summary --------------------------------------------------

const TRIGGER_PHRASE: Record<AutomationTriggerType, string> = {
  CONVERSATION_CREATED: 'a customer messages for the first time',
  NEW_MESSAGE: 'a customer sends a message',
  FOLLOW_UP_DUE: 'a follow-up is due',
  TASK_DUE: 'a task is due',
};

function quoteList(words: string[], joiner: string): string {
  const quoted = words.map((w) => `"${w}"`);
  if (quoted.length <= 1) return quoted.join('');
  return `${quoted.slice(0, -1).join(', ')} ${joiner} ${quoted[quoted.length - 1]}`;
}

export function rulePhrase(rule: Rule, buttonTitles: Record<string, string>): string {
  const value = rule.keywords[0] ?? '';
  switch (rule.kind) {
    case 'keywords':
      return `the message contains ${quoteList(rule.keywords, 'or')}`;
    case 'exact':
      return `the message is exactly "${value}"`;
    case 'contains':
      return `the message contains "${value}"`;
    case 'not_contains':
      return `the message doesn't contain "${value}"`;
    case 'button':
      return `they tap "${buttonTitles[value] ?? value}"`;
    case 'media':
      return `they send a ${(MEDIA_TYPES.find((m) => m.value === value)?.label ?? value).toLowerCase()}`;
  }
}

function actionPhrase(
  a: ActionDraft,
  lookups: { flowName?: (key: string) => string | undefined },
): string {
  switch (a.kind) {
    case 'reply': {
      const body = textOf(a.text).trim();
      const short = body.length > 40 ? `${body.slice(0, 39)}…` : body;
      const extra =
        a.replyMode === 'buttons' && a.buttons.length
          ? ` with ${a.buttons.length} button${a.buttons.length === 1 ? '' : 's'}`
          : a.replyMode === 'list'
            ? ' with a list'
            : '';
      return `reply "${short}"${extra}`;
    }
    case 'image':
      return 'send an image';
    case 'template':
      return `send the "${a.templateName}" template`;
    case 'flow':
      return `start the "${lookups.flowName?.(a.flowKey) ?? a.flowKey}" conversation`;
    case 'lead':
      return 'add them as a lead';
    case 'quotation':
      return 'make an AI quotation';
  }
}

/** "When a customer sends a message and it contains "price" or "rate", reply "…" and add them as a lead." */
export function describeAutomation(
  input: {
    triggerTypes?: AutomationTriggerType[];
    conditions?: Pick<
      AutomationConditionResource,
      'groupIndex' | 'position' | 'field' | 'operator' | 'value'
    >[];
    actions?: Pick<AutomationActionResource, 'actionType' | 'config' | 'position'>[];
  },
  lookups: {
    flowName?: (key: string) => string | undefined;
    buttonTitles?: Record<string, string>;
  } = {},
): string {
  // Tolerate list items from an API that doesn't send conditions/actions yet.
  const conditions = input.conditions ?? [];
  const actionList = input.actions ?? [];
  const when = (input.triggerTypes ?? []).map((t) => TRIGGER_PHRASE[t] ?? t);
  let sentence = `When ${when.join(' or ') || 'triggered'}`;

  const groups = groupConditions(conditions as AutomationConditionResource[]);
  if (groups.length) {
    const simple =
      groups.length === 1 || groups.every((g) => g.length === 1)
        ? groups.flat().map((c) => conditionToRule(c))
        : null;
    if (simple && simple.every((r): r is Rule => r !== null)) {
      const joiner = groups.length === 1 ? ' and ' : ' or ';
      sentence += ` and ${simple.map((r) => rulePhrase(r, lookups.buttonTitles ?? {})).join(joiner)}`;
    } else {
      sentence += ' and custom rules match';
    }
  }

  const actions = [...actionList]
    .sort((a, b) => a.position - b.position)
    .map((a) => actionPhrase(actionFromResource(a), lookups));
  const then =
    actions.length <= 1
      ? actions.join('')
      : `${actions.slice(0, -1).join(', ')} and ${actions[actions.length - 1]}`;
  return `${sentence}, ${then || 'do nothing'}.`;
}

/** Every button/list option id defined by these automations, with its label - powers the "Customer tapped…" picker. */
export function knownReplyOptions(
  automations: {
    name: string;
    actions?: Pick<AutomationActionResource, 'actionType' | 'config'>[];
  }[],
): { id: string; title: string; from: string }[] {
  const out = new Map<string, { id: string; title: string; from: string }>();
  for (const a of automations) {
    for (const action of a.actions ?? []) {
      const draft = actionFromResource(action as AutomationActionResource);
      if (draft.kind !== 'reply') continue;
      const options =
        draft.replyMode === 'buttons'
          ? draft.buttons
          : draft.replyMode === 'list'
            ? draft.sections.flatMap((s) => s.rows)
            : [];
      for (const o of options) {
        if (!out.has(o.id)) out.set(o.id, { id: o.id, title: textOf(o.title), from: a.name });
      }
    }
  }
  return [...out.values()];
}

/** `{{1}}`, `{{2}}`, ... placeholders in an approved template's BODY component. */
export function templateVariableKeys(components: unknown): string[] {
  if (!Array.isArray(components)) return [];
  const body = components.find(
    (c): c is { type: string; text?: string } =>
      typeof c === 'object' && c !== null && (c as { type?: string }).type === 'BODY',
  );
  const keys = new Set<string>();
  for (const m of body?.text?.matchAll(/\{\{\s*(\d+)\s*\}\}/g) ?? []) keys.add(m[1]!);
  return [...keys].sort((a, b) => Number(a) - Number(b));
}

// --- Automation templates (platform library) ---------------------------------

/**
 * The form's When / If / Then as a template definition: no channel (a
 * template belongs to no workspace), and "add as a lead" without a pipeline
 * / stage - the workspace picks those when it uses the template.
 */
export function templateDefinitionFromForm(state: AutomationFormState): {
  triggers: { triggerType: AutomationTriggerType }[];
  conditions: NonNullable<CreateAutomationInput['conditions']>;
  actions: {
    position: number;
    actionType: AutomationActionResource['actionType'];
    config: unknown;
  }[];
} {
  const { triggers, conditions, actions } = definitionFromForm(state);
  return {
    triggers,
    conditions: (conditions ?? []) as NonNullable<CreateAutomationInput['conditions']>,
    actions: actions.map((a) => {
      if (a.actionType !== 'CREATE_LEAD') return a;
      const { pipelineId: _p, stageId: _s, ...rest } = a.config as Record<string, unknown>;
      return { ...a, config: rest };
    }),
  };
}

/** `validateForm` for a template: no name (the template has its own), no pipeline / stage. */
export function validateTemplateDefinition(state: AutomationFormState): Record<string, string> {
  const errors = validateForm({ ...state, name: 'template' });
  for (const key of Object.keys(errors)) {
    if (
      key === 'name' ||
      key === 'description' ||
      /^actions\/\d+\/(pipelineId|stageId)$/.test(key)
    ) {
      delete errors[key];
    }
  }
  return errors;
}

/** A server field error on a template (`definition/actions/0/config/text`) keyed like the editor's (`actions/0/text`). */
export function editorErrorKey(serverKey: string): string {
  return serverKey.replace(/^definition\//, '').replace(/^(actions\/\d+)\/config\//, '$1/');
}
